import type {
  PropsWithChildren,
  PropsWithChildrenAndClassName,
  TooltipColorProps,
  TooltipComparison,
  TooltipValueProps,
} from './types'

import {
  Text,
  TextColor,
  TextSize,
  TextWeight,
} from '@moment/design-system-ui/text'
import { Fragment } from 'react'

import styles from '@/app/styles/components/tooltip.module.css'

export function TooltipContainer({
  children,
  className = '',
}: PropsWithChildrenAndClassName) {
  return (
    <div className={`${className} ${styles['moment-tooltip']}`}>{children}</div>
  )
}

export function TooltipHeader({ children }: PropsWithChildren) {
  return (
    <div className="px-3 py-1.75 flex flex-row justify-between">{children}</div>
  )
}

export function TooltipHeaderPrimary({ children }: PropsWithChildren) {
  return (
    <Text
      size={TextSize.Small}
      weight={TextWeight.Strong}
      color={TextColor.Primary}
    >
      {children}
    </Text>
  )
}

export function TooltipHeaderSecondary({ children }: PropsWithChildren) {
  return (
    <Text size={TextSize.Small} color={TextColor.Secondary}>
      {children}
    </Text>
  )
}

export function TooltipDivider() {
  return (
    <div className="h-[1px] border-b border-primary-border dark:border-tooltip-border-dark w-full" />
  )
}

export function TooltipValueContainer({ children }: PropsWithChildren) {
  return (
    <div className="flex flex-row gap-1.75 w-full px-3 pt-2 pb-1.75">
      {children}
    </div>
  )
}

export function TooltipColumn({
  children,
  className = '',
}: PropsWithChildrenAndClassName) {
  return <div className={`flex flex-col gap-1 ${className}`}>{children}</div>
}

const DIMMED_OPACITY = 0.3

export function TooltipValue({
  label,
  value,
  dimmed,
  icon,
  semanticLabel,
}: TooltipValueProps) {
  return (
    // biome-ignore lint/a11y/useAriaPropsSupportedByRole: Bulk suppression as part of biome migration
    <div
      className="flex flex-row justify-between gap-5 transition-opacity duration-200 ease"
      style={{ opacity: dimmed ? DIMMED_OPACITY : 1 }}
      aria-label={semanticLabel}
    >
      <Text
        size={TextSize.Small}
        color={TextColor.Secondary}
        className="text-ellipsis overflow-hidden text-nowrap max-w-[200px]"
      >
        {label}
      </Text>
      <div className="flex flex-row items-center gap-0.5">
        {icon}
        <Text
          size={TextSize.Small}
          color={TextColor.Primary}
          className="text-right"
        >
          {value}
        </Text>
      </div>
    </div>
  )
}

export function TooltipColor({ color, colorClass, dimmed }: TooltipColorProps) {
  return (
    <div className="flex flex-col items-center justify-center h-full">
      <div
        className={`w-1.75 h-1.75 mt-[-1px] rounded-xs transition-opacity duration-200 ease ${colorClass || ''}`}
        style={{
          ...(color ? { backgroundColor: color } : {}),
          opacity: dimmed ? DIMMED_OPACITY : 1,
        }}
      />
    </div>
  )
}

// Side-by-side comparison table: a metric-label column followed by one value
// column per source. Sources head the columns; metrics label the rows.
export function TooltipComparisonTable({
  comparison,
}: {
  comparison: TooltipComparison
}) {
  const { columns, rows } = comparison
  const gridTemplateColumns = `auto repeat(${columns.length}, minmax(max-content, 1fr))`

  return (
    <div
      className="grid gap-x-5 gap-y-1 w-full px-3 pt-2 pb-1.75 items-center"
      style={{ gridTemplateColumns }}
    >
      {/* Header row: empty corner cell, then a source label per column. */}
      <span />
      {columns.map((column, index) => (
        <div
          key={`header-${index}`}
          className="flex flex-row items-center justify-end gap-1"
        >
          {(column.color || column.colorClass) && (
            <TooltipColor color={column.color} colorClass={column.colorClass} />
          )}
          <Text
            size={TextSize.Small}
            color={TextColor.Primary}
            className="text-right"
          >
            {column.label}
          </Text>
        </div>
      ))}

      {rows.map((row, rowIndex) => (
        <Fragment key={`row-${rowIndex}`}>
          <Text
            size={TextSize.Small}
            color={TextColor.Secondary}
            className="text-ellipsis overflow-hidden text-nowrap"
          >
            {row.label}
          </Text>
          {row.values.map((value, colIndex) => (
            <Text
              key={`cell-${rowIndex}-${colIndex}`}
              size={TextSize.Small}
              color={TextColor.Primary}
              className="text-right"
            >
              {value}
            </Text>
          ))}
        </Fragment>
      ))}
    </div>
  )
}

export function TooltipFooter({ children }: PropsWithChildren) {
  // Don't wrap in <Text>: some footers are already a <p> (e.g.
  // CashflowTargetStatusText), and <p>-in-<p> is invalid nesting.
  return (
    <div className="px-3 py-1.75 flex flex-row justify-between text-xs text-secondary">
      {children}
    </div>
  )
}
